import * as THREE from 'three';

const BASE_RADIUS = 2.6;
const BASE_HEIGHT = 0.45;
const TERMINAL_HEIGHT = 1.55;
const ANTENNA_HEIGHT = 6.4;
const INTERACT_OFFSET_Z = -1.6; // in front of the terminal facing +Z

/**
 * Tiny outpost dropped on Caelis as the surface objective for the first
 * landing tutorial. Carries a wide platform, a terminal pillar with a
 * glowing screen, and an antenna mast — composed entirely from THREE
 * primitives so it costs nothing to build.
 *
 * Stays in the scene root after the tutorial step completes; the scene
 * tears it down on dispose.
 *
 * The interaction point is exposed separately so the caller (the
 * GameDirector) can do its own proximity check without having to
 * reach into the model graph.
 */
export class CaelisMiniBase {
  readonly object3D: THREE.Group;
  readonly position: THREE.Vector3;
  readonly interactionPoint: THREE.Vector3;

  private readonly screenMat: THREE.MeshBasicMaterial;
  private readonly antennaTipMat: THREE.MeshBasicMaterial;
  private readonly antennaLight: THREE.PointLight;
  private readonly haloMat: THREE.MeshBasicMaterial;
  private elapsed = 0;
  private disposed = false;

  // Cached materials/geometries so dispose can release everything.
  private readonly disposables: Array<{
    geometry?: THREE.BufferGeometry;
    material?: THREE.Material;
  }> = [];

  constructor(position: THREE.Vector3) {
    this.position = position.clone();
    this.object3D = new THREE.Group();
    this.object3D.position.copy(this.position);

    this.screenMat = new THREE.MeshBasicMaterial({
      color: 0xb4e3ff,
      transparent: true,
      opacity: 0.95
    });
    this.antennaTipMat = new THREE.MeshBasicMaterial({
      color: 0xffd389,
      transparent: true,
      opacity: 0.9
    });
    this.haloMat = new THREE.MeshBasicMaterial({
      color: 0xb4e3ff,
      transparent: true,
      opacity: 0.18,
      side: THREE.DoubleSide,
      depthWrite: false
    });

    this.buildBase();
    this.buildTerminal();
    this.buildAntenna();
    this.buildHalo();

    this.antennaLight = new THREE.PointLight(0xffd389, 0.7, 18, 2.0);
    this.antennaLight.position.set(0, ANTENNAS_LIGHT_Y, 0);
    this.object3D.add(this.antennaLight);

    this.interactionPoint = this.position
      .clone()
      .add(new THREE.Vector3(0, 0, INTERACT_OFFSET_Z));
  }

  /** Tick the small idle animations — antenna blink, screen pulse,
   *  ground halo throb. Cheap enough to call from the surface update
   *  loop every frame. */
  update(dt: number): void {
    if (this.disposed) return;
    this.elapsed += dt;
    const screenPulse = 0.7 + 0.3 * Math.sin(this.elapsed * 1.6);
    this.screenMat.opacity = 0.65 + 0.3 * screenPulse;
    const antennaPulse = 0.55 + 0.45 * Math.sin(this.elapsed * 3.2);
    this.antennaTipMat.opacity = 0.5 + 0.45 * antennaPulse;
    this.antennaLight.intensity = 0.45 + 0.55 * antennaPulse;
    this.haloMat.opacity = 0.14 + 0.12 * (0.5 + 0.5 * Math.sin(this.elapsed * 2.1));
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const d of this.disposables) {
      d.geometry?.dispose();
      d.material?.dispose();
    }
    this.disposables.length = 0;
    this.object3D.parent?.remove(this.object3D);
  }

  // ---- private builders ---------------------------------------------

  private buildBase(): void {
    // Wide flat platform — pale rose to fit the Caelis salt aesthetic
    // while reading as something built rather than naturally formed.
    const baseGeo = new THREE.CylinderGeometry(
      BASE_RADIUS,
      BASE_RADIUS * 1.05,
      BASE_HEIGHT,
      24
    );
    const baseMat = new THREE.MeshStandardMaterial({
      color: 0xc7b6b0,
      roughness: 0.55,
      metalness: 0.18
    });
    const base = new THREE.Mesh(baseGeo, baseMat);
    base.position.y = BASE_HEIGHT / 2;
    this.object3D.add(base);
    this.disposables.push({ geometry: baseGeo, material: baseMat });

    // Edge accent ring — soft cyan strip along the rim so the platform
    // pops at distance.
    const ringGeo = new THREE.TorusGeometry(BASE_RADIUS * 0.99, 0.06, 12, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x9fd9ff,
      transparent: true,
      opacity: 0.6
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = BASE_HEIGHT + 0.02;
    this.object3D.add(ring);
    this.disposables.push({ geometry: ringGeo, material: ringMat });

    // Stair lip on the front to face the terminal — gives the player
    // a clear "approach this side" cue.
    const stepGeo = new THREE.BoxGeometry(2.4, 0.2, 0.7);
    const stepMat = new THREE.MeshStandardMaterial({
      color: 0xa3938e,
      roughness: 0.7
    });
    const step = new THREE.Mesh(stepGeo, stepMat);
    step.position.set(0, 0.1, BASE_RADIUS - 0.2);
    this.object3D.add(step);
    this.disposables.push({ geometry: stepGeo, material: stepMat });
  }

  private buildTerminal(): void {
    // Pillar — narrow rectangular column rising off the platform.
    const pillarGeo = new THREE.BoxGeometry(0.95, TERMINAL_HEIGHT, 0.55);
    const pillarMat = new THREE.MeshStandardMaterial({
      color: 0x4a4d5d,
      roughness: 0.45,
      metalness: 0.6
    });
    const pillar = new THREE.Mesh(pillarGeo, pillarMat);
    pillar.position.set(0, BASE_HEIGHT + TERMINAL_HEIGHT / 2, -BASE_RADIUS * 0.4);
    this.object3D.add(pillar);
    this.disposables.push({ geometry: pillarGeo, material: pillarMat });

    // Screen — emissive panel facing +Z (toward the player when they
    // approach the front of the platform).
    const screenGeo = new THREE.PlaneGeometry(0.7, 0.55);
    const screen = new THREE.Mesh(screenGeo, this.screenMat);
    screen.position.set(0, BASE_HEIGHT + TERMINAL_HEIGHT * 0.62, -BASE_RADIUS * 0.4 + 0.281);
    this.object3D.add(screen);
    this.disposables.push({ geometry: screenGeo, material: this.screenMat });

    // Tiny dish-frame on top of the terminal — purely cosmetic, helps
    // the silhouette read at distance.
    const capGeo = new THREE.CylinderGeometry(0.45, 0.5, 0.18, 16);
    const capMat = new THREE.MeshStandardMaterial({
      color: 0x6c7280,
      roughness: 0.4,
      metalness: 0.7
    });
    const cap = new THREE.Mesh(capGeo, capMat);
    cap.position.set(0, BASE_HEIGHT + TERMINAL_HEIGHT + 0.09, -BASE_RADIUS * 0.4);
    this.object3D.add(cap);
    this.disposables.push({ geometry: capGeo, material: capMat });
  }

  private buildAntenna(): void {
    // Mast — slender cylinder that towers above the terminal so the
    // base reads from far away.
    const mastGeo = new THREE.CylinderGeometry(0.08, 0.12, ANTENNA_HEIGHT, 12);
    const mastMat = new THREE.MeshStandardMaterial({
      color: 0x394050,
      roughness: 0.4,
      metalness: 0.65
    });
    const mast = new THREE.Mesh(mastGeo, mastMat);
    mast.position.set(0, BASE_HEIGHT + ANTENNA_HEIGHT / 2, BASE_RADIUS * 0.55);
    this.object3D.add(mast);
    this.disposables.push({ geometry: mastGeo, material: mastMat });

    // Cross-bar struts — three thin bars near the top.
    const strutGeo = new THREE.BoxGeometry(1.2, 0.06, 0.06);
    const strutMat = new THREE.MeshStandardMaterial({
      color: 0x4a4d5d,
      roughness: 0.5
    });
    for (let i = 0; i < 3; i++) {
      const strut = new THREE.Mesh(strutGeo, strutMat);
      strut.position.set(
        0,
        BASE_HEIGHT + ANTENNA_HEIGHT * (0.55 + i * 0.16),
        BASE_RADIUS * 0.55
      );
      this.object3D.add(strut);
    }
    this.disposables.push({ geometry: strutGeo, material: strutMat });

    // Glowing tip — soft amber sphere so the player spots the marker
    // even when looking down at the terrain.
    const tipGeo = new THREE.SphereGeometry(0.18, 12, 10);
    const tip = new THREE.Mesh(tipGeo, this.antennaTipMat);
    tip.position.set(
      0,
      BASE_HEIGHT + ANTENNA_HEIGHT + 0.18,
      BASE_RADIUS * 0.55
    );
    this.object3D.add(tip);
    this.disposables.push({ geometry: tipGeo, material: this.antennaTipMat });
  }

  private buildHalo(): void {
    // Soft ground halo — anchors the base visually on the terrain.
    const haloGeo = new THREE.RingGeometry(BASE_RADIUS + 0.4, BASE_RADIUS + 2.0, 48);
    const halo = new THREE.Mesh(haloGeo, this.haloMat);
    halo.rotation.x = -Math.PI / 2;
    halo.position.y = 0.04;
    this.object3D.add(halo);
    this.disposables.push({ geometry: haloGeo, material: this.haloMat });
  }
}

const ANTENNAS_LIGHT_Y = BASE_HEIGHT + ANTENNA_HEIGHT * 0.95;
